import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import StatStrip from "../components/dashboard/StatStrip";
import UpgradeNudge from "../components/dashboard/UpgradeNudge";
import EmailStatsBlock from "../components/dashboard/EmailStatsBlock";
import FollowUpsPanel from "../components/dashboard/FollowUpsPanel";
import KitViewsCard from "../components/dashboard/KitViewsCard";
import BrandViewersCard from "../components/dashboard/BrandViewersCard";
import OnboardingProgress from "../components/dashboard/OnboardingProgress";
import WelcomeLap from "../components/dashboard/WelcomeLap";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Building2, Mail, ArrowUpRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [planUsage, setPlanUsage] = useState(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(() =>
    sessionStorage.getItem("pitchd_upgrade_nudge_dismissed") === "1");

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
    api.get("/outreach/stats").then(({ data }) => setEmailStats(data)).catch(() => {});
    api.get("/plan/limits").then(({ data }) => setPlanUsage(data)).catch(() => {});
  }, []);

  const dismissNudge = () => {
    sessionStorage.setItem("pitchd_upgrade_nudge_dismissed", "1");
    setNudgeDismissed(true);
  };

  // Compute which quota (if any) is ≥80% full for Free users
  const upgradeNudge = (() => {
    if (!planUsage || planUsage.plan !== "free" || nudgeDismissed) return null;
    const { limits, usage } = planUsage;
    const checks = [
      { key: "brands", label: "brands", used: usage.brands, limit: limits.brands },
      { key: "media_kits", label: "media kits", used: usage.media_kits, limit: limits.media_kits },
      { key: "monthly_outreach", label: "outreach emails this month", used: usage.monthly_outreach, limit: limits.monthly_outreach },
    ];
    let worst = null;
    for (const c of checks) {
      if (c.limit === -1 || c.limit === 0) continue;
      const pct = c.used / c.limit;
      if (pct >= 0.8 && (!worst || pct > worst.pct)) worst = { ...c, pct };
    }
    return worst;
  })();

  return (
    <DashboardLayout>
      <WelcomeLap />
      <div className="p-6 md:p-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="label-eyebrow">Command center</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="dashboard-title">
              Hey, {user?.full_name?.split(" ")[0] || "Athlete"}.
            </h1>
            <p className="text-ink-muted mt-2 text-sm">Here's where your sponsorship game stands today.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/app/media-kit" className="btn-volt inline-flex items-center gap-2 text-xs" data-testid="quick-mediakit-btn">
              <Sparkles className="w-4 h-4" /> Generate media kit
            </Link>
            <Link to="/app/brands" className="btn-ghost-line inline-flex items-center gap-2 text-xs" data-testid="quick-addbrand-btn">
              <Building2 className="w-4 h-4" /> Add brand
            </Link>
            <Link to="/app/outreach" className="btn-ghost-line inline-flex items-center gap-2 text-xs" data-testid="quick-outreach-btn">
              <Mail className="w-4 h-4" /> Create outreach
            </Link>
          </div>
        </div>

        <UpgradeNudge nudge={upgradeNudge} onDismiss={dismissNudge} />
        <OnboardingProgress />

        {!stats ? (
          <div className="label-eyebrow">Loading stats…</div>
        ) : (
          <>
            <StatStrip stats={stats} />

            <div className="grid md:grid-cols-3 gap-3 mt-3">
              <div className="surface p-6 md:col-span-2">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <div className="label-eyebrow">Pipeline by stage</div>
                    <h2 className="font-heading text-2xl uppercase mt-1">Sponsorship CRM</h2>
                  </div>
                  <Link to="/app/crm" className="text-xs uppercase tracking-widest text-volt inline-flex items-center gap-1">
                    Open board <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
                  {Object.entries(stats.by_status).map(([k, v]) => (
                    <div key={k} className="border border-line p-3 text-center">
                      <div className="font-heading text-2xl">{v}</div>
                      <div className="mt-1 text-[10px] font-mono uppercase tracking-normal text-ink-muted leading-tight">{k}</div>
                    </div>
                  ))}
                </div>
                <EmailStatsBlock emailStats={emailStats} />
              </div>

              <div className="space-y-3">
                <BrandViewersCard />
                <KitViewsCard />
                <FollowUpsPanel stats={stats} />
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
